import React, { useState, useRef, useContext } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { DataObjectContainer } from '../dataObjects/DataObjectContextManager';
import { BoundTextbox, BoundLabel, BoundDropdownList, PageableList, RenderConditionally } from '../dataObjects/BoundControls';
import { Popup, Confirmation } from '../core/Popup';
import { Upload, XSquare, Plus, Sun, Code, Share2, Trash2, Eye} from 'react-feather'; 
import { Card } from '../core/Card';
import { ApplicationInfo } from '../components/ApplicationInfo';
import { UserContext } from '../infrastructure/Contexts';
import { ApiDataAccess } from '../infrastructure/ApiDataAccess';
import { CapabilitiesContext } from '../infrastructure/Contexts';
import { CapabilityName } from '../infrastructure/Constants';

export const ApplicationPage = () => {
    const capabilities = useContext(CapabilitiesContext);
    const loader = capabilities.getCapability(CapabilityName.Loader);
    const context = useRef(null);
    const navigate = useNavigate();
    const popup = useRef(null);
    const confirmation = useRef(null);
    const list = useRef(null);

    const onSaved = async () => {
        list.current.refresh();
    }

    const cleanup = async () => {
        confirmation.current.show('This will remove unused images and vocabulary. Do you want to proceed', 'Cleanup',
            async () => {
                var da1 = new ApiDataAccess('api/dataobjects/applicationImageDataObject');
                await da1.post('/optimize');

                var da2 = new ApiDataAccess('api/dataobjects/translationDataObject');
                await da2.post('/optimize');
            }
        );
    }

    const onUploadClick = async (event) => {
        try {
            confirmation.current.show('Do you want to ignore the domain specific data for this import?', 'Import',
                async () => {
                    loader.show();
                    var file = event.target.files[0];
                    const payload = new FormData();
                    payload.append('formFile', file);
                    await fetch('/api/exchange/import?ignoreDomainData=true', { method: "POST", body: payload });                    
                    list.current.refresh();
                },
                async () => {
                    loader.show();
                    var file = event.target.files[0];
                    const payload = new FormData();
                    payload.append('formFile', file);
                    await fetch('/api/exchange/import?ignoreDomainData=false', { method: "POST", body: payload });
                    list.current.refresh();
                }
            );
        }
        catch (ex) {
            console.log(ex);
            alert('error');
        }

        loader.hide();
    }


    return (
        <div>
            <Popup ref={popup}>
                <NewApplicationPopupContent popup={popup} onSaved={onSaved} />
            </Popup>

            <Confirmation ref={confirmation} />

            <Card title='Applications' subtitle='The following applications are available'>      
                <DataObjectContainer ref={context} dataObjectName={'applicationDataObject'} >

                    <button className='btn btn-primary' onClick={() => { popup.current.show(); }}><Plus className='icon' /><span className='text'>New</span></button>
                    <button className='btn btn-tertiary-outline pull-right' onClick={() => { cleanup() }}><Sun className='icon' /><span className='text'>Cleanup</span></button>
                    <button className='btn btn-tertiary-outline pull-right mobile-hidden' onClick={() => { document.getElementById('btn_upload').click(); }}><Upload className='icon' /><span className='text'>Import</span></button>

                    <input className='display-none' id='btn_upload' type="file" accept=".json" name="myImage" onChange={onUploadClick} />

                    <PageableList pageSize={20} ref={list}
                        onRenderHeaderRow={() => {
                            return (
                                <div className='row caption' >
                                    <div className='cell mobile-hidden'>Client Id</div>
                                    <div className='cell'>Name</div>
                                    <div className='cell'>Default Country</div>
                                    <div className='cell mobile-hidden'>Default Language</div>
                                    <div className='cell mobile-hidden'>User</div>                                    
                                </div>
                            )
                        }}
                        onRenderRow={(key, item, references) => {
                            return (
                                <div className='row selectable' key={`row${key}`}>
                                    <div className='cell mobile-hidden' onClick={() => { navigate(`/applications/${key}`, { replace: false }); }}>{key}</div>
                                    <div className='cell' onClick={() => { navigate(`/applications/${key}`, { replace: false }); }}>{item.name}</div>
                                    <div className='cell' onClick={() => { navigate(`/applications/${key}`, { replace: false }); }}>
                                        <span className={`fi fi-${references.dataSources.defaultCultureId.find(e => e.id == item.defaultCultureId).countryCode}`}></span>                                        
                                    </div>
                                    <div className='cell mobile-hidden' onClick={() => { navigate(`/applications/${key}`, { replace: false }); }}>{references.dataSources.defaultCultureId.find(e => e.id == item.defaultCultureId).languageName}</div>
                                    <div className='cell mobile-hidden' onClick={() => { navigate(`/applications/${key}`, { replace: false }); }}>{item.createUser}</div>                                    
                                </div>
                            )
                        }} />

                </DataObjectContainer>
            </Card>
            
        </div>
    );
}


export const NewApplicationPopupContent = ({ popup, onSaved }) => {

    const context = useRef(null);
    const [valid, setValid] = useState(false);

    const onSaveClick = async () => {
        popup.current.close();
        var response = await context.current.submitAsync();
        if (onSaved) {
            onSaved(response);
        }
    }

    return (
        <DataObjectContainer ref={context} dataObjectName={'applicationDataObject'} onValidated={valid => { setValid(valid) }} >
            <div className='topSection'>
                <div className='left'></div>
                <div className='center'><h4>New Application</h4></div>
                <div className='right'><XSquare className='icon btn-close clickable' onClick={() => { popup.current.close() }} /></div>
            </div>

            <div className='contentSection'>
                    <label htmlFor='name'>Name</label>
                    <BoundTextbox propertyName={'name'} />

                    <label htmlFor='defaultCultureId'>Default Culture</label>
                    <BoundDropdownList propertyName={'defaultCultureId'} />
            </div>

            <div className='footerSection'>
                <button className='btn btn-primary btn-modal' onClick={() => { popup.current.close(); }}>Cancel</button>
                <button className='btn btn-secondary btn-modal' disabled={!valid} onClick={onSaveClick}>Save</button>
            </div>
        </DataObjectContainer>
    );
}




export const ApplicationDetailPage = () => {

    const { applicationKey } = useParams();

    return (
        <div>
            <ApplicationInfo applicationKey={applicationKey} showUserInfo={true} />

            <TranslationSection applicationKey={applicationKey} />

            <ImageSection applicationKey={applicationKey} />

        </div>
    );
}

export const ImageSection = ({ applicationKey }) => {

    const context = useRef(null);
    const popup = useRef(null);
    const navigate = useNavigate();
    const list = useRef(null);
    const userContext = useContext(UserContext);

    const showTagsModal = () => {
        popup.current.show();
    }

    return (
        <Card title='Images' subtitle='Available Images for this Application'>

            <Popup ref={popup}>
                <ImageTagModalContent popup={popup} applicationId={applicationKey} />
            </Popup>

            <button className='btn btn-tertiary-outline' onClick={() => { showTagsModal() }}><Code className='icon'/><span className='text'>New Script Tag</span></button>
            <DataObjectContainer ref={context} dataObjectName={'applicationImageStatisticDataObject'} >
                <PageableList ref={list} pageSize={20} defaultText='No images have been registered yet for this application'
                    filter={{ applicationclientid: `${applicationKey}`, sourceculture: `${userContext.cultureString}` }}
                    onRenderHeaderRow={() => {
                        return (
                            <div className='row caption' >
                                <div className='cell'>Source</div>                                
                                <div className='cell'>Images</div>
                            </div>
                        )
                    }}
                    onRenderRow={(key, item, references, idx) => {
                        return (
                            <div className='row selectable' key={`row${idx}`} onClick={() => { navigate(`/applications/${key}/source/${item.sourceCultureId}/images`, { replace: false }); }}>
                                <div className='cell'>
                                    <span className={`fi fi-${references.dataSources.sourceCultureId.find(e => e.id == item.sourceCultureId).countryCode}`}></span>
                                    {references.dataSources.sourceCultureId.find(e => e.id == item.sourceCultureId).languageName}
                                </div>
                                <div className='cell'>{item.total}</div>

                            </div>
                        )
                    }} />
            </DataObjectContainer>
        </Card>        
        )
}


export const TranslationSection = ({ applicationKey }) => {

    const context = useRef(null);
    const popup = useRef(null);
    const confirmation = useRef(null);
    const navigate = useNavigate();
    const list = useRef(null);
    const [sourceCulture, setSourceCulture] = useState(null);
    const [targetCulture, setTargetCulture] = useState(null);
    const userContext = useContext(UserContext);
    const capabilities = useContext(CapabilitiesContext);
    const notification = capabilities.getCapability(CapabilityName.Notification);

    const handleLearnChange = async (checked, item) => {

        item.learn = checked;
        await context.current.submitItemAsync(item.clientId, item);
        list.current.refresh();
    }

    const handleEnabledChange = async (checked, item) => {
        item.enabled = checked;
        await context.current.submitItemAsync(item.clientId, item);
        list.current.refresh();
    }

    const showTagsModal = (item) => {
        var sourceCultureId = item ? item.sourceCultureId : 'en-US';
        var targetCultureId = item ? item.targetCultureId : 'en-US';

        setSourceCulture(sourceCultureId);
        setTargetCulture(targetCultureId);
        popup.current.show();
    }

    const getReferenceItem = (dataSourceReferences, propertyName, id) => {
        var item = dataSourceReferences[propertyName].find(e => e.id == id);
        return item ? item : null;
    }

    const deleteTranslations = async (clientId, sourceCultureId, targetCultureId) => {
        confirmation.current.show('This will delete all translation for the selected language. The vocabulary will need to be provided by the client application. Do you want to continue?', 'Delete Translations', async () => {
            var dataAccess = new ApiDataAccess('/api/dataobjects/translationDataObject');
            await dataAccess.delete(`/clientId/${clientId}/sourceCulture/${sourceCultureId}/targetCulture/${targetCultureId}`);
            list.current.refresh();        
        });
    }


    const crossTrain = async () => {
        confirmation.current.show('This will apply copy the source vocabulary to all languages. Do you want to continue?', 'Cross Train', async () => {
            var dataAccess = new ApiDataAccess('/api/dataobjects/translationDataObject');
            var count = await dataAccess.post(`/clientId/${applicationKey}/sourceCulture/${userContext.cultureString}/crosstrain`);
            notification.show("info", "Cross Train", `Training is complete. ${count} translations have been created.`);
            list.current.refresh();        
        });
    }


    return (
        <Card title='Translations' subtitle='Available Translations for this Application'>
            <Popup ref={popup}>
                <TranslateTagModalContent popup={popup} applicationId={applicationKey} sourceCultureId={sourceCulture} targetCultureId={targetCulture} />
            </Popup>

            <Confirmation ref={confirmation} />

            <button className='btn btn-tertiary-outline' onClick={() => { showTagsModal(); }}><Code className='icon' /><span className='text'>New Script Tag</span></button>
            <button className='btn btn-tertiary-outline pull-right' onClick={() => { crossTrain(); }}><Share2 className='icon' /><span className='text'>Cross Train</span></button>


            <DataObjectContainer ref={context} dataObjectName={'translationStatisticDataObject'} >
                <PageableList ref={list} pageSize={20} defaultText='No translations have been registered yet for this application'
                    filter={{
                        applicationclientid: `${applicationKey}`, sourceculture: `${userContext.cultureString}` }}
                    onRenderHeaderRow={() => {
                        return (
                            <div className='row caption' >
                                <div className='cell'>Source</div>
                                <div className='cell'>Target</div>
                                <div className='cell mobile-hidden'>Translations</div>
                                <div className='cell mobile-hidden'>Learn</div>
                                <div className='cell mobile-hidden'>Enabled</div>
                                <div className='cell mobile-hidden text-align-right'>&nbsp;</div>
                                <div className='cell mobile-hidden text-align-right'>&nbsp;</div>
                            </div>
                        )
                    }}
                    onRenderRow={(key, item, references, idx) => {
                        return (
                            <div className='row selectable' key={`row${idx}`}>

                                <div className='cell' onClick={() => { navigate(`/applications/${key}/source/${item.sourceCultureId}/translations`, { replace: false }); }}>
                                    <span className={`fi fi-${getReferenceItem(references.dataSources, 'sourceCultureId', item.sourceCultureId) ? getReferenceItem(references.dataSources, 'sourceCultureId', item.sourceCultureId).countryCode : ''}`}></span>
                                    {getReferenceItem(references.dataSources, 'sourceCultureId', item.sourceCultureId) ? getReferenceItem(references.dataSources, 'sourceCultureId', item.sourceCultureId).languageName : item.sourceCultureId}
                                </div>

                                <div className='cell' onClick={() => { navigate(`/applications/${key}/source/${item.sourceCultureId}/target/${item.targetCultureId}/translations`, { replace: false }); }}>
                                    <span className={`fi fi-${getReferenceItem(references.dataSources, 'targetCultureId', item.targetCultureId) ? getReferenceItem(references.dataSources, 'targetCultureId', item.targetCultureId).countryCode : ''}`}></span>
                                    {getReferenceItem(references.dataSources, 'targetCultureId', item.targetCultureId) ? getReferenceItem(references.dataSources, 'targetCultureId', item.targetCultureId).languageName : item.targetCultureId }
                                </div>

                                <div className='cell mobile-hidden' onClick={() => { navigate(`/applications/${key}/source/${item.sourceCultureId}/target/${item.targetCultureId}/translations`, { replace: false }); }}>{item.total}</div>                                

                                <div className='cell mobile-hidden'>
                                    <label className='checkboxContainer'>
                                        <input type="checkbox" checked={item.learn} onChange={evt => { handleLearnChange(evt.target.checked, item) }} />
                                        <div className='caption' />
                                        <span className="checkmark"></span>
                                    </label>
                                </div>
                                <div className='cell mobile-hidden'>
                                    <label className='checkboxContainer'>
                                        <input type="checkbox" checked={item.enabled} onChange={evt => { handleEnabledChange(evt.target.checked, item) }} />
                                        <div className='caption'/>
                                        <span className="checkmark"></span>
                                    </label>
                                </div>
                                <div className='cell mobile-hidden text-align-right'>
                                    {item.validated < item.total &&
                                        <button className='btn sm btn-tertiary-outline' onClick={() => { navigate(`/applications/${key}/source/${item.sourceCultureId}/target/${item.targetCultureId}/review`, { replace: false }); }}><Eye className='icon' /> Review</button>
                                    }
                                </div>
                                <div className='cell mobile-hidden text-align-right'>
                                    <button className='btn sm btn-primary-outline' onClick={() => { deleteTranslations(applicationKey, item.sourceCultureId, item.targetCultureId) }}><Trash2 className='icon' /> Delete</button>
                                </div>
                            </div>
                        )
                    }} />
            </DataObjectContainer>
        </Card>
        )
}


export const ImageTagModalContent = ({ popup, applicationId }) => {

    const context = useRef(null);
    const [valid, setValid] = useState(false);
    const [id, setId] = useState(null);

    const onSaveClick = async () => {
        var response = await context.current.submitAsync();
        setId(response.value);
    }

    return (
        <div>

            <div className='topSection'>
                <div className='left'></div>
                <div className='center'><h4>Script Tag Creator</h4></div>
                <div className='right'><XSquare className='icon btn-close clickable' onClick={() => { popup.current.close() }} /></div>
            </div>

            {!id &&
                <DataObjectContainer ref={context} dataObjectName={'imageTagSelectionDataObject'} defaultData={{ applicationId: applicationId }} onValidated={valid => { setValid(valid) }} >
                    <div className='contentSection'>
                        <label htmlFor='dataType'>Data Type</label>
                        <BoundDropdownList propertyName={'dataType'} />

                        <label htmlFor='defaultText'>Description</label>
                        <BoundTextbox propertyName={'defaultText'} />

                        <label htmlFor='category'>Category</label>
                        <BoundDropdownList propertyName={'categoryId'} />
                    </div>

                    <div className='footerSection'>
                        <button className='btn btn-primary btn-modal' onClick={() => { popup.current.close(); }}>Cancel</button>
                        <button className='btn btn-secondary btn-modal' disabled={!valid} onClick={onSaveClick}>Create</button>
                    </div>
                </DataObjectContainer>
            }

            {id &&
                <DataObjectContainer dataObjectName={'imageTagSelectionDataObject'} dataObjectId={id} onValidated={valid => { setValid(valid) }} >
                    <div className='contentSection'>
                        <label htmlFor='dataType'>Unique Key</label>
                        <p className='code'><BoundLabel propertyName={'uniqueKey'} /></p>

                        <label htmlFor='dataType'>Tag Text</label>
                        <p className='code'><BoundLabel propertyName={'javaScript'} /></p>
                    </div>

                    <div className='footerSection'>
                        <button className='btn btn-primary btn-modal' onClick={() => { popup.current.close(); }}>Cancel</button>
                        <button className='btn btn-secondary btn-modal' disabled={!valid} onClick={() => { setId(null) }}>New</button>
                    </div>
                </DataObjectContainer>
            }

        </div>
    );
}


export const TranslateTagModalContent = ({ popup, applicationId, sourceCultureId, targetCultureId }) => {

    const context = useRef(null);
    const [valid, setValid] = useState(false);
    const [id, setId] = useState(null);

    const onSaveClick = async () => {
        var response = await context.current.submitAsync();        
        setId(response.value);
    }

    return (
        <div>

            <div className='topSection'>
                <div className='left'></div>
                <div className='center'><h4>Script Tag Creator</h4></div>
                <div className='right'><XSquare className='icon btn-close clickable' onClick={() => { popup.current.close() }} /></div>
            </div>

            {!id &&
                <DataObjectContainer ref={context} dataObjectName={'languageTagSelectionDataObject'} defaultData={{ applicationId: applicationId, sourceCultureId: sourceCultureId, targetCultureId: targetCultureId }} onValidated={valid => { setValid(valid) }} >
                    <div className='contentSection'>
                        <label htmlFor='dataType'>Data Type</label>
                        <BoundDropdownList propertyName={'dataType'} />

                        <label htmlFor='defaultText'>Default Text / Description</label>
                        <BoundTextbox propertyName={'defaultText'} />

                        <label htmlFor='category'>Category</label>
                        <BoundDropdownList propertyName={'categoryId'} />
                    </div>

                    <div className='footerSection'>
                        <button className='btn btn-primary btn-modal' onClick={() => { popup.current.close(); }}>Cancel</button>
                        <button className='btn btn-secondary btn-modal' disabled={!valid} onClick={onSaveClick}>Create</button>
                    </div>
                </DataObjectContainer>
            }

            {id &&
                <DataObjectContainer dataObjectName={'languageTagSelectionDataObject'} dataObjectId={id} onValidated={valid => { setValid(valid) }} >
                    <div className='contentSection'>
                        <label htmlFor='dataType'>Unique Key</label>
                        <p className='code'><BoundLabel propertyName={'uniqueKey'} /></p>

                        <label htmlFor='dataType'>JavaScript</label>
                        <p className='code'><BoundLabel propertyName={'javaScript'} /></p>

                        <label htmlFor='dataType'>Tag</label>
                        <p className='code'><BoundLabel propertyName={'tagCode'} /></p>

                        <RenderConditionally propertyName={'exists'} value={true}>
                            <p className='bold color-orange'>The vocabulary for this category exists already and was reused!</p>
                        </RenderConditionally>    

                    </div>

                    <div className='footerSection'>
                        <button className='btn btn-primary btn-modal' onClick={() => { popup.current.close(); }}>Cancel</button>
                        <button className='btn btn-secondary btn-modal' disabled={!valid} onClick={() => { setId(null) }}>New</button>
                    </div>
                </DataObjectContainer>
            }

        </div>
    );
}